import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import ComprehensiveKYPDocument from '../pdf/ComprehensiveKYPDocument';
import { downloadKYPPDF, downloadKYPPDFFromFirestore, composeRealtimeKYPData, generateKYPPDF } from '../../utils/pdfUtils';
import { getLeadGeneratorById } from '../../services/leadGeneratorService';

// Stable error boundary outside component to prevent remount on each render
class PDFErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) {
    console.error('PDFViewer crashed:', error, info);
    if (typeof this.props.onCrash === 'function') this.props.onCrash(error);
  }
  render() { return this.state.hasError ? null : this.props.children; }
}

const KYPPreviewModal = ({ isOpen, onClose, application, kypData, leadGeneratorData: passedLeadGeneratorData, canDownload = true, actions = [] }) => {
  const [isGeneratingPro, setIsGeneratingPro] = useState(false);
  const [loadingLeadGenerator, setLoadingLeadGenerator] = useState(false);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [renderError, setRenderError] = useState(null);

  // Freeze inputs on open to avoid re-renders from parent updates
  const deepClone = (obj) => (obj ? JSON.parse(JSON.stringify(obj)) : obj);
  const [frozenApplication, setFrozenApplication] = useState(null);
  const [frozenKypData, setFrozenKypData] = useState(null);
  const [frozenLeadGeneratorData, setFrozenLeadGeneratorData] = useState(passedLeadGeneratorData || null);
  const [isInitialized, setIsInitialized] = useState(false);
  const fetchStartedRef = useRef(null);

  const applicationId = frozenApplication?.applicationId || frozenKypData?.applicationDetails?.applicationId;

  // Fetch lead generator data when modal opens (only if not passed as prop)
  // Freeze incoming data when modal opens
  useEffect(() => {
    if (!isOpen) {
      // Reset frozen data only on close to avoid re-init loops
      setIsInitialized(false);
      setFrozenApplication(null);
      setFrozenKypData(null);
      setFrozenLeadGeneratorData(passedLeadGeneratorData || null);
      setFallbackMode(false);
      setPreviewUrl(null);
      return;
    }
    
    // Only initialize once per modal open session
    if (isInitialized) return;
    
    setFrozenApplication(deepClone(application));
    try {
      const composed = application ? composeRealtimeKYPData(application) : null;
      setFrozenKypData(composed || deepClone(kypData) || {});
    } catch (e) {
      console.warn('composeRealtimeKYPData failed for preview, using provided kypData.', e);
      setFrozenKypData(deepClone(kypData) || {});
    }
    setFrozenLeadGeneratorData(deepClone(passedLeadGeneratorData) || null);
    // reset fetch guard for this open session
    fetchStartedRef.current = null;
    setIsInitialized(true);
  }, [isOpen, isInitialized, application, kypData, passedLeadGeneratorData]);

  // Fetch lead generator data only once per open
  useEffect(() => {
    const fetchLeadGeneratorData = async () => {
      if (!isOpen || !isInitialized) return;
      const lgId = frozenApplication?.leadGenerator?.leadGeneratorId;
      if (frozenLeadGeneratorData || !lgId) return;
      if (fetchStartedRef.current === lgId) return; // Already fetching/fetched this open
      fetchStartedRef.current = lgId;
      setLoadingLeadGenerator(true);
      try {
        const result = await getLeadGeneratorById(lgId);
        if (result.success) {
          setFrozenLeadGeneratorData(result.leadGenerator);
        } else {
          console.warn('Failed to fetch lead generator data:', result.error);
        }
      } catch (error) {
        console.error('Error fetching lead generator data:', error);
      } finally {
        setLoadingLeadGenerator(false);
      }
    };
    fetchLeadGeneratorData();
  }, [isOpen, isInitialized, frozenApplication?.leadGenerator?.leadGeneratorId, frozenLeadGeneratorData]);

  // Compose a live KYP snapshot from the provided application object for preview
  const previewKyp = useMemo(() => frozenKypData || {}, [frozenKypData]);

  // Memoize the PDF component to prevent re-renders
  // Stabilize props identity to PDF component to avoid internal rerenders
  const stableAppRef = useRef(null);
  const stableKypRef = useRef(null);
  const stableLgRef = useRef(null);
  if (frozenApplication && stableAppRef.current !== frozenApplication) stableAppRef.current = frozenApplication;
  if (previewKyp && stableKypRef.current !== previewKyp) stableKypRef.current = previewKyp;
  if (frozenLeadGeneratorData && stableLgRef.current !== frozenLeadGeneratorData) stableLgRef.current = frozenLeadGeneratorData;

  const pdfComponent = useMemo(() => {
    if (!stableAppRef.current || !stableKypRef.current) return null;
    return (
      <ComprehensiveKYPDocument
        application={stableAppRef.current}
        kypData={stableKypRef.current}
        leadGeneratorData={stableLgRef.current}
      />
    );
  }, [stableAppRef.current, stableKypRef.current, stableLgRef.current]);

  // When a crash happens, fall back to iframe preview
  const propsOnCrash = async (error) => {
    setRenderError(error?.message || String(error));
    setFallbackMode(true);
    try {
      const appForBlob = frozenApplication || application || null;
      const kypForBlob = previewKyp && Object.keys(previewKyp || {}).length > 0
        ? previewKyp
        : (appForBlob ? composeRealtimeKYPData(appForBlob) : {});
      if (!appForBlob) throw new Error('Missing application data for PDF');
      // Generate fallback with professional template first; include lead generator data
      const res = await generateKYPPDF(appForBlob, kypForBlob, { template: 'professional', leadGeneratorData: frozenLeadGeneratorData });
      if (res?.success && res.blob) {
        const url = URL.createObjectURL(res.blob);
        setPreviewUrl(url);
      }
    } catch (e) {
      console.error('Fallback PDF blob generation failed:', e);
    }
  };

  // Cleanup blob URL on close/unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const downloadPreviewPDF = async () => {
    try {
      setIsGeneratingPro(true);
      if (applicationId) {
        const res = await downloadKYPPDFFromFirestore(applicationId, { template: 'comprehensive' });
        if (!res?.success) throw new Error(res?.error || 'Failed to generate professional PDF');
      } else {
        const fileName = `KYP_Comprehensive_${Date.now()}.pdf`;
        const res = await downloadKYPPDF(frozenApplication, previewKyp, fileName, { template: 'comprehensive' });
        if (!res?.success) throw new Error(res?.error || 'Failed to generate professional PDF');
      }
    } catch (e) {
      console.error('Professional PDF error:', e);
      alert('Failed to generate professional PDF. Please try again.');
    } finally {
      setIsGeneratingPro(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '20px',
        maxWidth: '95vw',
        maxHeight: '95vh',
        overflow: 'auto',
        position: 'relative',
        width: 'fit-content'
      }}>
        {/* Header with close and download buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '2px solid #1e4d72',
          paddingBottom: '10px'
        }}>
          <h2 style={{ color: '#1e4d72', margin: 0 }}>Comprehensive KYP Authorization Letter</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            {Array.isArray(actions) && actions.length > 0 && actions.map((action, idx) => (
              <button
                key={idx}
                onClick={action.onClick}
                disabled={!!action.disabled}
                style={{
                  backgroundColor: action.disabled ? '#94a3b8' : (action.variant === 'danger' ? '#dc3545' : action.variant === 'success' ? '#16a34a' : '#0ea5e9'),
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '5px',
                  cursor: action.disabled ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {action.label}
              </button>
            ))}
            <button
              onClick={downloadPreviewPDF}
              disabled={isGeneratingPro || !canDownload}
              style={{
                backgroundColor: isGeneratingPro || !canDownload ? '#94a3b8' : '#0ea5e9',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: (isGeneratingPro || !canDownload) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {isGeneratingPro ? '⏳ Building...' : '📄 Download KYP PDF'}
            </button>
            <button
              onClick={onClose}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Loading indicator for lead generator data */}
        {loadingLeadGenerator && (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            fontSize: '14px',
            color: '#666'
          }}>
            Loading lead generator information...
          </div>
        )}

        {/* Embedded comprehensive KYP PDF viewer with safe fallback */}
        {!fallbackMode ? (
          <div style={{ width: '80vw', height: '80vh', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
            <PDFErrorBoundary onCrash={propsOnCrash}>
              {pdfComponent && !loadingLeadGenerator ? (
                <PDFViewer width="100%" height="100%" showToolbar={false}>
                  {pdfComponent}
                </PDFViewer>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', background: '#f9fafb', color: '#6b7280', fontSize: 14 }}>
                  {loadingLeadGenerator ? 'Loading lead generator information…' : 'Preparing KYP preview…'}
                </div>
              )}
            </PDFErrorBoundary>
          </div>
        ) : (
          <div style={{ width: '80vw', height: '80vh', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '8px', background: '#fffbe6', color: '#8a6d3b', fontSize: '13px' }}>
              PDF live preview fell back due to a viewer error{renderError ? `: ${renderError}` : ''}. Using safe preview.
            </div>
            {previewUrl ? (
              <iframe title="KYP Preview" src={`${previewUrl}#toolbar=0&navpanes=0`} style={{ border: 'none', width: '100%', height: '100%' }} />
            ) : (
              <div style={{ padding: '16px', fontSize: '14px' }}>Preparing preview…</div>
            )}
          </div>
        )}

        {/* Footer Instructions */}
        <div style={{
          marginTop: '15px',
          padding: '10px',
          backgroundColor: '#f8f9fa',
          borderRadius: '5px',
          fontSize: '14px',
          color: '#666'
        }}>
          <strong>📋 Instructions:</strong> Review the comprehensive 5-page KYP authorization letter above. This document includes patient details, medical information, policy terms, and comprehensive conditions. {canDownload ? 'Click "Download KYP PDF" to save the complete document.' : 'Download will be enabled after you acknowledge the KYP.'}
        </div>
      </div>
    </div>
  );
};

export default KYPPreviewModal;
